import { useTranslation } from 'react-i18next';

import { tidy, type LayoutElement, type Source } from './layout';

interface Props {
  element: LayoutElement;
  entityKind: string;
  onChange: (element: LayoutElement) => void;
  onRemove: () => void;
}

const SOURCES: Source[] = [
  'code',
  'url',
  'title',
  'subtitle',
  'extra',
  'mpn',
  'manufacturer',
  'serial_number',
  'quantity_at_print',
  'date',
  'datetime',
  'time',
  'field_1',
  'field_2',
  'field_3',
  'field_4',
  'field_5',
  'field_6',
];

/**
 * The selected block, in numbers.
 *
 * Beside the canvas rather than instead of it. Dragging answers "where", and
 * these answer the questions dragging cannot: which value the block draws, how
 * many lines a name may wrap onto, how small the type is allowed to go before
 * the renderer gives up and cuts the text off.
 */
export function ElementForm({ element, entityKind, onChange, onRemove }: Props) {
  const { t } = useTranslation();
  const set = (changes: Partial<LayoutElement>) => onChange({ ...element, ...changes });
  const number = (value: string) => tidy(Number(value) || 0);

  const textish = element.type === 'text';
  const availableSources = entityKind === 'freeform'
    ? SOURCES.filter((source) => source.startsWith('field_') || source === 'date' || source === 'datetime' || source === 'time')
    : SOURCES.filter((source) => !source.startsWith('field_'));

  return (
    <div className="designer__form">
      <div className="designer__grid">
        <label>
          {t('labels.designer.x')}
          <input
            type="number"
            step={0.1}
            value={element.x}
            onChange={(event) => set({ x: number(event.target.value) })}
          />
        </label>
        <label>
          {t('labels.designer.y')}
          <input
            type="number"
            step={0.1}
            value={element.y}
            onChange={(event) => set({ y: number(event.target.value) })}
          />
        </label>
        <label>
          {t('labels.designer.width')}
          <input
            type="number"
            step={0.1}
            value={element.w}
            onChange={(event) => set({ w: number(event.target.value) })}
          />
        </label>
        <label>
          {t('labels.designer.height')}
          <input
            type="number"
            step={0.1}
            value={element.h}
            onChange={(event) => set({ h: number(event.target.value) })}
          />
        </label>
      </div>

      {element.type !== 'line' && (
        <label>
          {t('labels.designer.source')}
          <select
            value={element.text === undefined ? (element.source ?? 'code') : '__text'}
            onChange={(event) => {
              const value = event.target.value;
              if (value === '__text') set({ text: element.text ?? '', source: undefined });
              else set({ source: value as Source, text: undefined });
            }}
          >
            {availableSources.map((source) => (
              <option key={source} value={source}>
                {t(`labels.designer.sources.${source}`)}
              </option>
            ))}
            {/* Fixed text is only meaningful for words. A QR of a caption is a
                QR of a caption — technically drawable, never wanted. */}
            {textish && <option value="__text">{t('labels.designer.ownText')}</option>}
          </select>
        </label>
      )}

      {element.text !== undefined && (
        <label>
          {t('labels.designer.ownText')}
          <input value={element.text} onChange={(event) => set({ text: event.target.value })} />
        </label>
      )}

      {textish && (
        <>
          <div className="designer__grid">
            <label>
              {t('labels.designer.size')}
              <input
                type="number"
                step={0.1}
                value={element.size_mm ?? 3}
                onChange={(event) => set({ size_mm: number(event.target.value) })}
              />
            </label>
            {/* How small the renderer may shrink the type before it starts
                cutting the text off instead. A name two points smaller is still
                a name; a name without its end is a different part. */}
            <label>
              {t('labels.designer.minSize')}
              <input
                type="number"
                step={0.1}
                value={element.min_size_mm ?? 2}
                onChange={(event) => set({ min_size_mm: number(event.target.value) })}
              />
            </label>
            <label>
              {t('labels.designer.lines')}
              <input
                type="number"
                min={1}
                max={6}
                value={element.lines ?? 1}
                onChange={(event) => set({ lines: Math.max(1, Number(event.target.value) || 1) })}
              />
            </label>
            <label>
              {t('labels.designer.align')}
              <select
                value={element.align ?? 'left'}
                onChange={(event) =>
                  set({ align: event.target.value as LayoutElement['align'] })
                }
              >
                <option value="left">{t('labels.designer.alignLeft')}</option>
                <option value="center">{t('labels.designer.alignCenter')}</option>
                <option value="right">{t('labels.designer.alignRight')}</option>
              </select>
            </label>
          </div>

          <label className="designer__check">
            <input
              type="checkbox"
              checked={Boolean(element.bold)}
              onChange={(event) => set({ bold: event.target.checked })}
            />
            {t('labels.designer.bold')}
          </label>
        </>
      )}

      <button type="button" className="designer__remove" onClick={onRemove}>
        {t('labels.designer.removeBlock')}
      </button>
    </div>
  );
}
